import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getEnumOptions } from '../../../framework/framework-enums';
import type { FrameworkAction, FrameworkArgument } from '../../../framework/framework.d';
import { useAllAvailableActions } from '../../../hooks/editor/useAllAvailableActions';
import { excludeGroupOnlyActions } from './hardwareParser';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { isFrameworkNumberArgType } from '../../../utils/frameworkArgTypes';
import {
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  isTupleArg,
  sequenceTupleRowsToArgValues,
  tupleMemberSchemaDefault,
  tupleMemberValueKey,
} from '../../../utils/tupleArg';
import { isUnsupportedSequenceTupleLiteral, parseSequenceTupleLiteral, sequenceTupleLiteralRowsAreEditable } from '../../Drawers/pythonArgParser';
import { scalarFrameworkArgIsValid, sequenceTupleRowsAreValid } from '../../../utils/sequenceTupleValidation';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import ActionSelector from '../../Drawers/Controller/ActionSelector';
import FrameworkActionArgumentFields from '../../Drawers/Controller/FrameworkActionArgumentFields';
import {
  clearRawActionArgValue,
  cloneRawActionArgValues,
  copyRawActionArgValues,
  findActionDefForLabel,
  getRawActionArgValue,
  updateRawActionArgValue,
} from './driveModeAutonomousActionUtils';

/** A flat-key prefix test for a sequence-of-tuples row key like `lines.0.text`. */
const isSequenceTupleRowKey = (key: string, argName: string): boolean =>
  key.startsWith(`${argName}.`) && /^\d+\./.test(key.slice(argName.length + 1));

/**
 * Hydrate a `Sequence[tuple[...]]` argument from `source` into `target` (a flat argValues map),
 * recording every written key in `copiedValueKeys` so raw metadata is carried along. Handles all
 * three input shapes the sensor pipeline can deliver: already-expanded row keys (`lines.0.*`), a raw
 * list literal under the arg name (`lines = "[(1, \"Ready\")]"` from parsed code — expanded when it is
 * editable, preserved verbatim when unsupported), or nothing (seed one default row when `seedWhenEmpty`).
 */
function hydrateSequenceTupleArgValues(
  frameworkArg: FrameworkArgument,
  source: Record<string, string>,
  target: Record<string, string>,
  copiedValueKeys: string[],
  seedWhenEmpty: boolean
): void {
  const argName = frameworkArg.name;
  copiedValueKeys.push(argName);
  const copyAll = (values: Record<string, string>) => {
    for (const [key, value] of Object.entries(values)) {
      target[key] = value;
      copiedValueKeys.push(key);
    }
  };

  const rowKeys = Object.keys(source ?? {}).filter((key) => isSequenceTupleRowKey(key, argName));
  if (rowKeys.length > 0) {
    for (const key of rowKeys) {
      target[key] = String(source[key] ?? '');
      copiedValueKeys.push(key);
    }
    return;
  }

  const rawLiteral = String(source?.[argName] ?? '').trim();
  const seqMembers = frameworkArg.members ?? [];
  if (rawLiteral) {
    if (sequenceTupleLiteralRowsAreEditable(rawLiteral, seqMembers)) {
      copyAll(sequenceTupleRowsToArgValues(frameworkArg, parseSequenceTupleLiteral(rawLiteral)));
      return;
    }
    if (isUnsupportedSequenceTupleLiteral(rawLiteral, seqMembers)) {
      target[argName] = rawLiteral; // preserve unsupported code verbatim
      return;
    }
    // An empty list (`[]`) falls through to zero rows.
  }
  if (seedWhenEmpty) {
    copyAll(defaultSequenceTupleRowArgValues(frameworkArg));
  }
}

/** Merge add/remove row-key changes for sequence args from `next` into `current`, leaving all other
 * (scalar/tuple/raw) keys untouched. Used by the batch `onArgValuesChange` setter. */
function mergeSequenceTupleRowChanges(
  current: Record<string, string>,
  next: Record<string, string>,
  actionDef: FrameworkAction | null
): Record<string, string> {
  const merged = { ...current };
  for (const arg of actionDef?.arguments ?? []) {
    const frameworkArg = arg as FrameworkArgument;
    if (!isSequenceTupleArg(frameworkArg)) continue;
    const argName = frameworkArg.name;
    // Drop the aggregate raw literal (if any) and stale row keys, then take the new rows.
    delete merged[argName];
    clearRawActionArgValue(merged, argName);
    for (const key of Object.keys(merged)) {
      if (isSequenceTupleRowKey(key, argName)) delete merged[key];
    }
    for (const key of Object.keys(next)) {
      if (isSequenceTupleRowKey(key, argName)) merged[key] = next[key];
    }
  }
  return merged;
}
import { SENSOR_ACTION_SIDES, SENSOR_TRIGGER_ARG_KEYS, SENSOR_TRIGGER_NAMES, type SensorActionSide } from './sensorMappingConstants';
import { getMinimalRevealScrollDelta } from './sensorMappingScrollUtils';

export type SensorActionDraft = {
  actionLabel: string;
  argValues: Record<string, string>;
  rawExpression?: string;
};

export type TriggerDraft = {
  triggerName: string;
  triggerArgs: Record<string, string>;
  rawTriggerArgsText?: string;
  onTrueActionLabel: string;
  onFalseActionLabel: string;
  onTrueArgValues: Record<string, string>;
  onFalseArgValues: Record<string, string>;
  onTrueActions?: SensorActionDraft[];
  onFalseActions?: SensorActionDraft[];
};

export type SensorMappingDraft = {
  triggers: TriggerDraft[];
};

export type SensorTriggerArgument = FrameworkArgument;
export type SensorTriggerOption = { name: string; arguments?: SensorTriggerArgument[] };
type ArgHydrationMode = 'display' | 'semantic';
type SensorMappingRow = {
  side: SensorActionSide;
  action: SensorActionDraft;
  actionIndex: number;
};

interface SensorMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (opts: { sensorVarName: string; draft: SensorMappingDraft }, options?: { closeDialog?: boolean }) => void;
  sensorOptions: Array<{
    sensorVarName: string;
    sensorTriggerOptions: SensorTriggerOption[];
  }>;
  initialSensorVarName?: string | null;
  initialTriggerIndex?: number | null;
  initialActionSide?: SensorActionSide | null;
  initialActionIndex?: number | null;
  initialDraftsBySensorVar?: Record<string, SensorMappingDraft>;
  onRefreshDraftFromCode?: (sensorVarName: string) => SensorMappingDraft | null;
}

const emptyAction = (): SensorActionDraft => ({ actionLabel: '', argValues: {} });

const normalizeAction = (draft: Partial<SensorActionDraft> | null | undefined): SensorActionDraft => {
  const argValues = cloneRawActionArgValues(draft?.argValues);
  return {
    actionLabel: String(draft?.actionLabel ?? ''),
    argValues,
    rawExpression: String(draft?.rawExpression ?? '').trim() || undefined,
  };
};

const actionListFromDraft = (
  draft: Partial<TriggerDraft>,
  actionsKey: 'onTrueActions' | 'onFalseActions',
  legacyLabelKey: 'onTrueActionLabel' | 'onFalseActionLabel',
  legacyArgsKey: 'onTrueArgValues' | 'onFalseArgValues'
): SensorActionDraft[] => {
  if (Array.isArray(draft[actionsKey])) {
    return (draft[actionsKey] ?? []).map((action) => normalizeAction(action));
  }

  const legacyLabel = String(draft[legacyLabelKey] ?? '');
  if (!legacyLabel) return [];
  const argValues = cloneRawActionArgValues(draft[legacyArgsKey] as Record<string, string> | undefined);
  return [
    {
      actionLabel: legacyLabel,
      argValues,
    },
  ];
};

const actionsForSide = (trigger: TriggerDraft, side: SensorActionSide): SensorActionDraft[] =>
  side === SENSOR_ACTION_SIDES.ON_TRUE ? trigger.onTrueActions ?? [] : trigger.onFalseActions ?? [];

const withLegacyActionFields = (trigger: TriggerDraft): TriggerDraft => {
  const onTrueFirst = trigger.onTrueActions?.[0] ?? emptyAction();
  const onFalseFirst = trigger.onFalseActions?.[0] ?? emptyAction();
  return {
    ...trigger,
    onTrueActionLabel: onTrueFirst.actionLabel,
    onTrueArgValues: onTrueFirst.argValues,
    onFalseActionLabel: onFalseFirst.actionLabel,
    onFalseArgValues: onFalseFirst.argValues,
  };
};

const sortedArgsKey = (args: Record<string, string>): string =>
  JSON.stringify(Object.fromEntries(Object.entries(args || {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))));

const triggerSignature = (trigger: TriggerDraft): string =>
  `${trigger.triggerName}|${String(trigger.rawTriggerArgsText || '').trim() || sortedArgsKey(trigger.triggerArgs)}`;

const hasConfiguredAction = (action: SensorActionDraft): boolean =>
  !!String(action.actionLabel || '').trim() || !!String(action.rawExpression || '').trim();

const isFollowPathAction = (name: string | undefined): boolean => name === 'follow_path_action';

const mappingRowsForTrigger = (trigger: TriggerDraft): SensorMappingRow[] => [
  ...(trigger.onTrueActions ?? []).map((action, actionIndex) => ({ side: SENSOR_ACTION_SIDES.ON_TRUE, action, actionIndex })),
  ...(trigger.onFalseActions ?? []).map((action, actionIndex) => ({ side: SENSOR_ACTION_SIDES.ON_FALSE, action, actionIndex })),
];

function revealNodeIfClipped(container: HTMLElement | null, target: HTMLElement | null): boolean {
  if (!container || !target) return false;
  const containerRect = container.getBoundingClientRect();
  if (containerRect.bottom <= containerRect.top) return false;
  const delta = getMinimalRevealScrollDelta(containerRect, target.getBoundingClientRect());
  if (Math.abs(delta) < 1) return false;
  container.scrollBy({ top: delta, behavior: 'auto' });
  return true;
}

const mergeConfiguredTriggers = (triggers: TriggerDraft[]): TriggerDraft[] => {
  const merged = new Map<string, TriggerDraft>();
  const result: TriggerDraft[] = [];
  triggers.forEach((trigger) => {
    if (!trigger.triggerName) {
      result.push(trigger);
      return;
    }
    const signature = triggerSignature(trigger);
    const existing = merged.get(signature);
    if (existing) {
      merged.set(
        signature,
        withLegacyActionFields({
          ...existing,
          onTrueActions: [...(existing.onTrueActions ?? []), ...(trigger.onTrueActions ?? [])],
          onFalseActions: [...(existing.onFalseActions ?? []), ...(trigger.onFalseActions ?? [])],
        })
      );
      return;
    }
    merged.set(signature, trigger);
    result.push(trigger);
  });

  return result.map((trigger) => (trigger.triggerName ? merged.get(triggerSignature(trigger)) ?? trigger : trigger));
};

const SensorMappingDialog: React.FC<SensorMappingDialogProps> = ({
  open,
  onClose,
  onSave,
  sensorOptions,
  initialSensorVarName,
  initialTriggerIndex,
  initialActionSide,
  initialActionIndex,
  initialDraftsBySensorVar,
  onRefreshDraftFromCode,
}) => {
  const { t } = useTranslation();
  const allAvailableActions = useAllAvailableActions();
  const selectableActionList = useMemo(() => excludeGroupOnlyActions(allAvailableActions), [allAvailableActions]);
  const projectPaths = useAppSelector((state) => state.project.loadedProject?.paths ?? []);
  const hasActionOptions = (selectableActionList || []).length > 0;

  const [selectedSensorVarName, setSelectedSensorVarName] = useState<string>('');
  const [selectedTriggerName, setSelectedTriggerName] = useState<string>('');
  const [triggers, setTriggers] = useState<TriggerDraft[]>([]);
  const [pendingSensorVarName, setPendingSensorVarName] = useState<string | null>(null);
  const draftsBySensorVarRef = useRef<Record<string, SensorMappingDraft>>({});
  const initialSaveJsonBySensorVarRef = useRef<Record<string, string>>({});
  const locallyCachedSensorVarsRef = useRef<Set<string>>(new Set());
  const triggerMappingRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const actionRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingAddedTriggerIndexRef = useRef<number | null>(null);
  const pendingRevealActionKeyRef = useRef<string | null>(null);
  const didApplyInitialFocusRef = useRef(false);
  const didInitializeOpenSessionRef = useRef(false);

  const ENUM_OPTIONS = useMemo(() => getEnumOptions(), []);
  const pathOptions = useMemo<Array<{ id: string; name: string }>>(
    () => projectPaths.map((p: { id: string; name: string }) => ({ id: p.id, name: p.name })),
    [projectPaths]
  );
  const selectedSensorOption = useMemo(
    () => (sensorOptions || []).find((s) => s.sensorVarName === selectedSensorVarName) ?? null,
    [selectedSensorVarName, sensorOptions]
  );
  const sensorTriggerOptions = useMemo(() => selectedSensorOption?.sensorTriggerOptions ?? [], [selectedSensorOption]);
  const optionNames = useMemo(() => new Set((sensorTriggerOptions || []).map((x) => x.name)), [sensorTriggerOptions]);
  const selectedTriggerDef = useMemo(
    () => (sensorTriggerOptions || []).find((option) => option.name === selectedTriggerName) ?? null,
    [selectedTriggerName, sensorTriggerOptions]
  );
  const selectedTriggerArgs = selectedTriggerDef?.arguments ?? [];
  const canAddThreshold = selectedTriggerArgs.length > 0;
  const visibleTriggerEntries = useMemo(() => {
    const entries = triggers
      .map((trigger, triggerIndex) => ({ trigger, triggerIndex }))
      .filter(({ trigger }) => trigger.triggerName === selectedTriggerName);
    return selectedTriggerArgs.length > 0 ? entries : entries.slice(0, 1);
  }, [selectedTriggerArgs.length, selectedTriggerName, triggers]);
  const initialActionFocusKey = useMemo(() => {
    if (typeof initialTriggerIndex !== 'number') return null;
    if (!initialActionSide) return null;
    if (typeof initialActionIndex !== 'number') return null;
    return `${initialTriggerIndex}-${initialActionSide}-${initialActionIndex}`;
  }, [initialActionIndex, initialActionSide, initialTriggerIndex]);
  const initialActionFocusSignature = useMemo(
    () => (open ? `${selectedSensorVarName}|${initialActionFocusKey ?? ''}` : ''),
    [initialActionFocusKey, open, selectedSensorVarName]
  );

  const parseMeters = useCallback((raw: unknown): number => {
    const n = Number(String(raw ?? '').trim());
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n));
  }, []);

  const defaultArgsForTrigger = useCallback(
    (triggerName: string): Record<string, string> => {
      const triggerDef = sensorTriggerOptions.find((option) => option.name === triggerName);
      const defaults: Record<string, string> = {};
      triggerDef?.arguments?.forEach((arg) => {
        const defaultVal = arg.defaultValue ?? arg.value;
        if (defaultVal !== undefined && defaultVal !== null && String(defaultVal) !== '') {
          defaults[arg.name] = String(defaultVal);
        }
      });
      return defaults;
    },
    [sensorTriggerOptions]
  );

  const actionDefForLabel = useCallback(
    (actionLabel: string): FrameworkAction | null => findActionDefForLabel(allAvailableActions, actionLabel),
    [allAvailableActions]
  );

  const argValuesForAction = useCallback(
    (actionDef: FrameworkAction | null, mode: ArgHydrationMode): Record<string, string> => {
      if (!actionDef?.arguments || actionDef.arguments.length === 0) return {};
      const values: Record<string, string> = {};
      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isTupleArg(frameworkArg)) {
          if (mode === 'semantic') return;
          for (const [memberIndex, member] of (frameworkArg.members || []).entries()) {
            const memberArg = member as FrameworkArgument;
            const enumOpts = isFrameworkNumberArgType(memberArg.type || '') ? undefined : ENUM_OPTIONS[memberArg.type || ''];
            const defaultValue = tupleMemberSchemaDefault(frameworkArg, memberIndex, memberArg);
            const memberKey = tupleMemberValueKey(frameworkArg.name, memberArg.name);
            if (defaultValue !== undefined && defaultValue !== null && String(defaultValue) !== '') {
              values[memberKey] = String(defaultValue);
            } else if (enumOpts && enumOpts.length > 0) {
              values[memberKey] = enumOpts[0].value;
            } else {
              values[memberKey] = '';
            }
          }
          return;
        }
        const defaultVal = frameworkArg.defaultValue ?? frameworkArg.value;
        if (defaultVal !== undefined && defaultVal !== null && String(defaultVal) !== '') {
          values[arg.name] = String(defaultVal);
          return;
        }

        if (mode === 'semantic') return;

        const enumOpts = isFrameworkNumberArgType(arg.type || '') ? undefined : ENUM_OPTIONS[arg.type || ''];
        if (isFollowPathAction(actionDef.name) && arg.name === 'path' && pathOptions.length > 0) {
          values[arg.name] = pathOptions[0].name;
        } else if (enumOpts && enumOpts.length > 0) {
          values[arg.name] = enumOpts[0].value;
        } else {
          values[arg.name] = '';
        }
      });
      return values;
    },
    [ENUM_OPTIONS, pathOptions]
  );

  const syncActionArgValues = useCallback(
    (draft: SensorActionDraft, mode: ArgHydrationMode = 'semantic'): SensorActionDraft => {
      const actionLabel = String(draft.actionLabel || '').trim();
      const rawExpression = String(draft.rawExpression || '').trim();
      if (!actionLabel) {
        return rawExpression ? { actionLabel: '', argValues: {}, rawExpression } : emptyAction();
      }
      const actionDef = actionDefForLabel(actionLabel);
      if (!actionDef) {
        return { actionLabel, argValues: cloneRawActionArgValues(draft.argValues) };
      }
      if (!actionDef.arguments || actionDef.arguments.length === 0) {
        return { actionLabel, argValues: {} };
      }

      const defaults = argValuesForAction(actionDef, mode);
      const nextValues: Record<string, string> = {};
      const copiedValueKeys: string[] = [];
      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          hydrateSequenceTupleArgValues(frameworkArg, draft.argValues ?? {}, nextValues, copiedValueKeys, mode === 'display');
          return;
        }
        copiedValueKeys.push(frameworkArg.name);
        if (isTupleArg(frameworkArg)) {
          const hasTupleRawValue =
            getRawActionArgValue(draft.argValues, frameworkArg.name) !== undefined ||
            Object.prototype.hasOwnProperty.call(draft.argValues ?? {}, frameworkArg.name);
          if (hasTupleRawValue) {
            nextValues[frameworkArg.name] = String(draft.argValues?.[frameworkArg.name] ?? '');
          }
          for (const member of frameworkArg.members || []) {
            const memberKey = tupleMemberValueKey(frameworkArg.name, member.name);
            copiedValueKeys.push(memberKey);
            if (Object.prototype.hasOwnProperty.call(draft.argValues ?? {}, memberKey)) {
              nextValues[memberKey] = String(draft.argValues?.[memberKey] ?? '');
            } else if (!hasTupleRawValue && Object.prototype.hasOwnProperty.call(defaults, memberKey)) {
              nextValues[memberKey] = defaults[memberKey];
            } else if (!hasTupleRawValue && mode === 'display') {
              nextValues[memberKey] = '';
            }
          }
          return;
        }
        const hasCurrent = Object.prototype.hasOwnProperty.call(draft.argValues ?? {}, arg.name);
        if (hasCurrent) {
          nextValues[arg.name] = String(draft.argValues?.[arg.name] ?? '');
          return;
        }
        if (Object.prototype.hasOwnProperty.call(defaults, arg.name)) {
          nextValues[arg.name] = defaults[arg.name];
          return;
        }
        if (mode === 'display') {
          nextValues[arg.name] = '';
        }
      });
      copyRawActionArgValues(draft.argValues, nextValues, copiedValueKeys);

      return { actionLabel, argValues: nextValues };
    },
    [actionDefForLabel, argValuesForAction]
  );

  const normalizeTrigger = useCallback(
    (draft: Partial<TriggerDraft>, mode: ArgHydrationMode = 'semantic'): TriggerDraft => {
      const defaultTrigger = sensorTriggerOptions[0]?.name ?? '';
      const rawName = String(draft.triggerName ?? '').trim();
      const triggerName = rawName.length === 0 ? defaultTrigger : optionNames.has(rawName) ? rawName : defaultTrigger;
      const onTrueActions = actionListFromDraft(draft, 'onTrueActions', 'onTrueActionLabel', 'onTrueArgValues').map((action) =>
        syncActionArgValues(action, mode)
      );
      const onFalseActions = actionListFromDraft(draft, 'onFalseActions', 'onFalseActionLabel', 'onFalseArgValues').map((action) =>
        syncActionArgValues(action, mode)
      );

      return withLegacyActionFields({
        triggerName,
        triggerArgs: { ...defaultArgsForTrigger(triggerName), ...(draft.triggerArgs ?? {}) },
        rawTriggerArgsText: String(draft.rawTriggerArgsText ?? '').trim() || undefined,
        onTrueActionLabel: '',
        onFalseActionLabel: '',
        onTrueArgValues: {},
        onFalseArgValues: {},
        onTrueActions,
        onFalseActions,
      });
    },
    [defaultArgsForTrigger, optionNames, sensorTriggerOptions, syncActionArgValues]
  );

  const displayArgValuesForAction = useCallback(
    (actionDef: FrameworkAction | null, argValues: Record<string, string>): Record<string, string> => {
      if (!actionDef?.arguments || actionDef.arguments.length === 0) return { ...(argValues ?? {}) };
      const defaults = argValuesForAction(actionDef, 'display');
      const displayValues: Record<string, string> = {};
      const copiedValueKeys: string[] = [];
      actionDef.arguments.forEach((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          // Display never seeds a phantom row; selection-time sync already seeded into the draft.
          hydrateSequenceTupleArgValues(frameworkArg, argValues ?? {}, displayValues, copiedValueKeys, false);
          return;
        }
        copiedValueKeys.push(frameworkArg.name);
        if (isTupleArg(frameworkArg)) {
          const hasTupleRawValue =
            getRawActionArgValue(argValues, frameworkArg.name) !== undefined ||
            Object.prototype.hasOwnProperty.call(argValues ?? {}, frameworkArg.name);
          if (hasTupleRawValue) {
            displayValues[frameworkArg.name] = String(argValues?.[frameworkArg.name] ?? '');
          }
          for (const member of frameworkArg.members || []) {
            const memberKey = tupleMemberValueKey(frameworkArg.name, member.name);
            copiedValueKeys.push(memberKey);
            if (Object.prototype.hasOwnProperty.call(argValues ?? {}, memberKey)) {
              displayValues[memberKey] = String(argValues?.[memberKey] ?? '');
            } else if (!hasTupleRawValue) {
              displayValues[memberKey] = defaults[memberKey] ?? '';
            }
          }
          return;
        }
        if (Object.prototype.hasOwnProperty.call(argValues ?? {}, arg.name)) {
          displayValues[arg.name] = String(argValues?.[arg.name] ?? '');
        } else {
          displayValues[arg.name] = defaults[arg.name] ?? '';
        }
      });
      copyRawActionArgValues(argValues, displayValues, copiedValueKeys);
      return displayValues;
    },
    [argValuesForAction]
  );

  const createEmptyTriggerForName = useCallback(
    (triggerName: string): TriggerDraft =>
      normalizeTrigger({
        triggerName,
        triggerArgs: defaultArgsForTrigger(triggerName),
        onTrueActions: [emptyAction()],
        onFalseActions: [],
      }),
    [defaultArgsForTrigger, normalizeTrigger]
  );

  const createEmptyTrigger = useCallback((): TriggerDraft => {
    const firstTriggerName = String(sensorTriggerOptions[0]?.name || '').trim();
    return normalizeTrigger({
      triggerName: firstTriggerName,
      triggerArgs: defaultArgsForTrigger(firstTriggerName),
      onTrueActions: [emptyAction()],
      onFalseActions: [],
    });
  }, [defaultArgsForTrigger, normalizeTrigger, sensorTriggerOptions]);

  const triggerNameForScreen = useCallback(
    (loadedTriggers: TriggerDraft[]): string => {
      const initialTriggerName = typeof initialTriggerIndex === 'number' ? String(loadedTriggers[initialTriggerIndex]?.triggerName ?? '').trim() : '';
      if (initialTriggerName && optionNames.has(initialTriggerName)) return initialTriggerName;

      const firstExistingTriggerName = String(loadedTriggers.find((trigger) => optionNames.has(trigger.triggerName))?.triggerName ?? '').trim();
      if (firstExistingTriggerName) return firstExistingTriggerName;

      return String(sensorTriggerOptions[0]?.name ?? '').trim();
    },
    [initialTriggerIndex, optionNames, sensorTriggerOptions]
  );

  const draftToTriggers = useCallback(
    (draft: SensorMappingDraft | null | undefined): TriggerDraft[] => {
      const fromTriggers = Array.isArray(draft?.triggers) ? draft.triggers : [];
      return mergeConfiguredTriggers(fromTriggers.map((triggerDraft) => normalizeTrigger(triggerDraft)));
    },
    [normalizeTrigger]
  );

  const finalizeTriggerForSave = useCallback(
    (trigger: TriggerDraft): TriggerDraft | null => {
      const next = normalizeTrigger(trigger);
      if (!next.triggerName) return null;
      const triggerDefaults = defaultArgsForTrigger(next.triggerName);
      const hasRawTriggerArgs = !!String(next.rawTriggerArgsText || '').trim();

      if (!hasRawTriggerArgs && (next.triggerName === SENSOR_TRIGGER_NAMES.GREATER_THAN || next.triggerName === SENSOR_TRIGGER_NAMES.LESS_THAN)) {
        const raw = String(next.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE] ?? '').trim();
        if (!raw) {
          next.triggerArgs = {
            ...(next.triggerArgs ?? {}),
            [SENSOR_TRIGGER_ARG_KEYS.DISTANCE]: triggerDefaults[SENSOR_TRIGGER_ARG_KEYS.DISTANCE] ?? '0',
          };
        }
      }
      if (!hasRawTriggerArgs && next.triggerName === SENSOR_TRIGGER_NAMES.BETWEEN) {
        const nearRaw = String(next.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR] ?? '').trim();
        const farRaw = String(next.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR] ?? '').trim();
        next.triggerArgs = {
          ...(next.triggerArgs ?? {}),
          [SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR]: nearRaw || triggerDefaults[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR] || '0',
          [SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR]: farRaw || triggerDefaults[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR] || '0',
        };
      }

      next.onTrueActions = (next.onTrueActions ?? []).filter(hasConfiguredAction);
      next.onFalseActions = (next.onFalseActions ?? []).filter(hasConfiguredAction);
      if (next.onTrueActions.length === 0 && next.onFalseActions.length === 0) return null;
      return withLegacyActionFields(next);
    },
    [defaultArgsForTrigger, normalizeTrigger]
  );

  const buildSaveTriggersFrom = useCallback(
    (sourceTriggers: TriggerDraft[]): TriggerDraft[] => {
      const merged = new Map<string, TriggerDraft>();
      sourceTriggers.forEach((trigger) => {
        const finalized = finalizeTriggerForSave(trigger);
        if (!finalized) return;
        const signature = triggerSignature(finalized);
        const existing = merged.get(signature);
        if (existing) {
          merged.set(
            signature,
            withLegacyActionFields({
              ...existing,
              onTrueActions: [...(existing.onTrueActions ?? []), ...(finalized.onTrueActions ?? [])],
              onFalseActions: [...(existing.onFalseActions ?? []), ...(finalized.onFalseActions ?? [])],
            })
          );
          return;
        }
        merged.set(signature, finalized);
      });
      return Array.from(merged.values());
    },
    [finalizeTriggerForSave]
  );

  const serializeSaveTriggers = useCallback(
    (sourceTriggers: TriggerDraft[]): string => JSON.stringify(buildSaveTriggersFrom(sourceTriggers)),
    [buildSaveTriggersFrom]
  );

  const loadTriggersForSensor = useCallback(
    (sensorVarName: string): TriggerDraft[] => {
      const cachedDraft = draftsBySensorVarRef.current[sensorVarName] ?? null;
      const hasLocalDraft = locallyCachedSensorVarsRef.current.has(sensorVarName);
      const fromCode = hasLocalDraft ? null : onRefreshDraftFromCode?.(sensorVarName) ?? null;
      const loaded = draftToTriggers(hasLocalDraft ? cachedDraft : fromCode ?? cachedDraft);
      return loaded.length > 0 ? loaded : [createEmptyTrigger()];
    },
    [createEmptyTrigger, draftToTriggers, onRefreshDraftFromCode]
  );

  // Apply the initial sensor and draft cache before paint so Select controls do not flash stale values.
  useLayoutEffect(() => {
    if (!open) {
      didInitializeOpenSessionRef.current = false;
      setSelectedSensorVarName((prev) => (prev ? '' : prev));
      setSelectedTriggerName((prev) => (prev ? '' : prev));
      setTriggers((prev) => (prev.length > 0 ? [] : prev));
      return;
    }
    if (didInitializeOpenSessionRef.current) return;

    didInitializeOpenSessionRef.current = true;
    draftsBySensorVarRef.current = { ...(initialDraftsBySensorVar ?? {}) };
    initialSaveJsonBySensorVarRef.current = {};
    locallyCachedSensorVarsRef.current = new Set();
    setPendingSensorVarName(null);
    const firstSensor = sensorOptions[0]?.sensorVarName ?? '';
    const nextSelected = initialSensorVarName || firstSensor;
    setSelectedSensorVarName((prev) => (prev === nextSelected ? prev : nextSelected));
  }, [open, initialDraftsBySensorVar, initialSensorVarName, sensorOptions]);

  useLayoutEffect(() => {
    if (!open) return;
    if (!selectedSensorVarName) {
      setTriggers((prev) => (prev.length > 0 ? [] : prev));
      setSelectedTriggerName((prev) => (prev ? '' : prev));
      return;
    }
    const loadedTriggers = loadTriggersForSensor(selectedSensorVarName);
    const nextTriggerName = triggerNameForScreen(loadedTriggers);
    const nextTriggers =
      nextTriggerName && !loadedTriggers.some((trigger) => trigger.triggerName === nextTriggerName)
        ? [...loadedTriggers, createEmptyTriggerForName(nextTriggerName)]
        : loadedTriggers;
    setSelectedTriggerName(nextTriggerName);
    setTriggers(nextTriggers);
    if (!(selectedSensorVarName in initialSaveJsonBySensorVarRef.current)) {
      initialSaveJsonBySensorVarRef.current[selectedSensorVarName] = serializeSaveTriggers(nextTriggers);
    }
  }, [createEmptyTriggerForName, loadTriggersForSensor, open, selectedSensorVarName, serializeSaveTriggers, triggerNameForScreen]);

  useEffect(() => {
    if (!open || !selectedTriggerName) return;
    if (!optionNames.has(selectedTriggerName)) return;
    setTriggers((prev) => {
      if (prev.some((trigger) => trigger.triggerName === selectedTriggerName)) return prev;
      return [...prev, createEmptyTriggerForName(selectedTriggerName)];
    });
  }, [createEmptyTriggerForName, open, optionNames, selectedTriggerName]);

  useEffect(() => {
    didApplyInitialFocusRef.current = false;
  }, [initialActionFocusSignature]);

  useEffect(() => {
    if (!open || !initialActionFocusKey || didApplyInitialFocusRef.current) return;
    const row = actionRowRefs.current[initialActionFocusKey];
    if (!row) return;

    didApplyInitialFocusRef.current = true;
    row.scrollIntoView({ block: 'center', behavior: 'auto' });
    const comboboxes = row.querySelectorAll<HTMLElement>('[role="combobox"]');
    const focusTarget = comboboxes[1] ?? comboboxes[0] ?? row.querySelector<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }, [initialActionFocusKey, open, triggers]);

  useEffect(() => {
    if (!open) return;
    const triggerIndex = pendingAddedTriggerIndexRef.current;
    if (triggerIndex === null) return;
    const mapping = triggerMappingRefs.current[triggerIndex];
    if (!mapping) return;

    pendingAddedTriggerIndexRef.current = null;
    revealNodeIfClipped(scrollContainerRef.current, mapping);
  }, [open, triggers]);

  useEffect(() => {
    if (!open) return;
    const actionKey = pendingRevealActionKeyRef.current;
    if (!actionKey) return;
    const row = actionRowRefs.current[actionKey];
    if (!row) return;

    pendingRevealActionKeyRef.current = null;
    revealNodeIfClipped(scrollContainerRef.current, row);
  }, [open, triggers]);

  const updateTriggerAt = useCallback(
    (triggerIndex: number, updater: (trigger: TriggerDraft) => TriggerDraft) => {
      setTriggers((prev) =>
        prev.map((trigger, index) => (index === triggerIndex ? withLegacyActionFields(normalizeTrigger(updater(trigger))) : trigger))
      );
    },
    [normalizeTrigger]
  );

  const setActionsForSide = useCallback(
    (trigger: TriggerDraft, side: SensorActionSide, nextActions: SensorActionDraft[]): TriggerDraft => {
      const filteredActions = nextActions.map((action) => syncActionArgValues(normalizeAction(action), 'semantic'));
      const next =
        side === SENSOR_ACTION_SIDES.ON_TRUE ? { ...trigger, onTrueActions: filteredActions } : { ...trigger, onFalseActions: filteredActions };
      return withLegacyActionFields(next);
    },
    [syncActionArgValues]
  );

  const updateActionAt = useCallback(
    (triggerIndex: number, side: SensorActionSide, actionIndex: number, updater: (action: SensorActionDraft) => SensorActionDraft) => {
      updateTriggerAt(triggerIndex, (trigger) => {
        const currentActions = actionsForSide(trigger, side);
        const editableActions = currentActions.length > 0 ? [...currentActions] : [emptyAction()];
        editableActions[actionIndex] = updater(editableActions[actionIndex] ?? emptyAction());
        return setActionsForSide(trigger, side, editableActions);
      });
    },
    [setActionsForSide, updateTriggerAt]
  );

  const removeActionAt = useCallback(
    (triggerIndex: number, side: SensorActionSide, actionIndex: number) => {
      updateTriggerAt(triggerIndex, (trigger) => {
        const currentActions = actionsForSide(trigger, side);
        if (currentActions.length === 0) return trigger;
        return setActionsForSide(
          trigger,
          side,
          currentActions.filter((_action, index) => index !== actionIndex)
        );
      });
    },
    [setActionsForSide, updateTriggerAt]
  );

  const handleMappingSideChange = useCallback(
    (triggerIndex: number, side: SensorActionSide, actionIndex: number, nextSide: SensorActionSide) => {
      if (side === nextSide) return;
      updateTriggerAt(triggerIndex, (trigger) => {
        const sourceActions = actionsForSide(trigger, side);
        const movedAction = sourceActions[actionIndex] ?? emptyAction();
        const nextSourceActions = sourceActions.filter((_action, index) => index !== actionIndex);
        const targetActions = actionsForSide(trigger, nextSide);
        pendingRevealActionKeyRef.current = `${triggerIndex}-${nextSide}-${targetActions.length}`;
        return setActionsForSide(setActionsForSide(trigger, side, nextSourceActions), nextSide, [...targetActions, movedAction]);
      });
    },
    [setActionsForSide, updateTriggerAt]
  );

  const handleTriggerArgChange = useCallback(
    (triggerIndex: number, name: string, value: string) => {
      updateTriggerAt(triggerIndex, (trigger) => ({
        ...trigger,
        triggerArgs: { ...(trigger.triggerArgs ?? {}), [name]: value },
        rawTriggerArgsText: undefined,
      }));
    },
    [updateTriggerAt]
  );

  const handleActionChange = useCallback(
    (triggerIndex: number, side: SensorActionSide, actionIndex: number, nextLabel: string) => {
      pendingRevealActionKeyRef.current = `${triggerIndex}-${side}-${actionIndex}`;
      updateActionAt(triggerIndex, side, actionIndex, () => syncActionArgValues({ actionLabel: nextLabel, argValues: {} }, 'display'));
    },
    [syncActionArgValues, updateActionAt]
  );

  const handleActionArgChange = useCallback(
    (triggerIndex: number, side: SensorActionSide, actionIndex: number, name: string, value: string) => {
      updateActionAt(triggerIndex, side, actionIndex, (action) => ({
        ...action,
        argValues: (() => {
          const nextValues = updateRawActionArgValue(action.argValues, name, value);
          const actionDef = actionDefForLabel(action.actionLabel);
          const tupleArg = (actionDef?.arguments || []).find((arg) =>
            isTupleArg(arg as FrameworkArgument)
              ? (arg as FrameworkArgument).members?.some((member) => tupleMemberValueKey(arg.name, member.name) === name)
              : false
          ) as FrameworkArgument | undefined;
          if (tupleArg) {
            delete nextValues[tupleArg.name];
            clearRawActionArgValue(nextValues, tupleArg.name);
          }
          // Editing a sequence row member supersedes any aggregate raw list literal under the arg name.
          const sequenceArg = (actionDef?.arguments || []).find(
            (arg) => isSequenceTupleArg(arg as FrameworkArgument) && isSequenceTupleRowKey(name, arg.name)
          ) as FrameworkArgument | undefined;
          if (sequenceArg) {
            delete nextValues[sequenceArg.name];
            clearRawActionArgValue(nextValues, sequenceArg.name);
          }
          return nextValues;
        })(),
      }));
    },
    [actionDefForLabel, updateActionAt]
  );

  const handleAddMapping = useCallback(
    (triggerIndex: number) => {
      updateTriggerAt(triggerIndex, (trigger) => {
        if (!trigger.triggerName) return trigger;
        const currentActions = actionsForSide(trigger, SENSOR_ACTION_SIDES.ON_TRUE);
        pendingRevealActionKeyRef.current = `${triggerIndex}-${SENSOR_ACTION_SIDES.ON_TRUE}-${currentActions.length}`;
        return setActionsForSide(trigger, SENSOR_ACTION_SIDES.ON_TRUE, [...currentActions, emptyAction()]);
      });
    },
    [setActionsForSide, updateTriggerAt]
  );

  const handleTriggerTypeScreenChange = useCallback((event: { target: { value: unknown } }) => {
    setSelectedTriggerName(String(event.target.value || ''));
  }, []);

  const handleAddThreshold = useCallback(() => {
    if (!selectedTriggerName) return;
    setTriggers((prev) => {
      pendingAddedTriggerIndexRef.current = prev.length;
      return [...prev, createEmptyTriggerForName(selectedTriggerName)];
    });
  }, [createEmptyTriggerForName, selectedTriggerName]);

  const handleRemoveMapping = useCallback(
    (triggerIndex: number) => {
      setTriggers((prev) => {
        const next = prev.filter((_trigger, index) => index !== triggerIndex);
        if (selectedTriggerName && !next.some((trigger) => trigger.triggerName === selectedTriggerName)) {
          return [...next, createEmptyTriggerForName(selectedTriggerName)];
        }
        return next.length > 0 ? next : [createEmptyTrigger()];
      });
    },
    [createEmptyTrigger, createEmptyTriggerForName, selectedTriggerName]
  );

  const renderDistanceArgSliders = useCallback(
    (tr: TriggerDraft, onArgChange: (name: string, value: string) => void) => {
      const triggerName = String(tr.triggerName || '').trim();
      const isBetween = triggerName === SENSOR_TRIGGER_NAMES.BETWEEN;
      const isGt = triggerName === SENSOR_TRIGGER_NAMES.GREATER_THAN;
      const isLt = triggerName === SENSOR_TRIGGER_NAMES.LESS_THAN;
      if (!isBetween && !isGt && !isLt) return null;

      const meterUnit = t('framework.units.meters.short', { defaultValue: 'm' });
      const formatMeters = (v: number) => `${v.toFixed(2)} ${meterUnit}`;
      const formatMeterEndpoint = (v: number) => `${v} ${meterUnit}`;
      const sliderOuterSx = { my: 1, width: '100%' };
      const sliderInnerSx = { width: '100%', px: '10px', boxSizing: 'border-box' };
      const sliderLabelSx = { mb: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1 };
      const sliderEndpointSx = { display: 'flex', justifyContent: 'space-between', color: 'text.secondary', mt: -0.5 };
      const sliderSx = {
        '& .MuiSlider-mark': { bgcolor: 'action.disabled' },
        '& .MuiSlider-markActive': { bgcolor: 'action.disabled' },
      } as const;

      if (isBetween) {
        const near = parseMeters(tr.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR]);
        const far = parseMeters(tr.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR]);
        const safeNear = Math.min(near, far);
        const safeFar = Math.max(near, far);

        return (
          <Box sx={sliderOuterSx}>
            <Box sx={sliderLabelSx}>
              <Typography variant="body2">
                {t('controlPanel.sensors.mapping.distance.rangeMeters', { defaultValue: 'Near and Far Distance' })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatMeters(safeNear)} - {formatMeters(safeFar)}
              </Typography>
            </Box>
            <Box sx={sliderInnerSx}>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[safeNear, safeFar]}
                marks={[{ value: 0 }, { value: 1 }]}
                sx={sliderSx}
                valueLabelDisplay="off"
                onChange={(_e, next) => {
                  const arr = Array.isArray(next) ? next : [Number(next), Number(next)];
                  const nextNear = Math.max(0, Math.min(1, Number(arr[0] ?? 0)));
                  const nextFar = Math.max(0, Math.min(1, Number(arr[1] ?? 0)));
                  onArgChange(SENSOR_TRIGGER_ARG_KEYS.DISTANCE_NEAR, String(Math.min(nextNear, nextFar)));
                  onArgChange(SENSOR_TRIGGER_ARG_KEYS.DISTANCE_FAR, String(Math.max(nextNear, nextFar)));
                }}
              />
              <Box sx={sliderEndpointSx}>
                <Typography variant="body2">{formatMeterEndpoint(0)}</Typography>
                <Typography variant="body2">{formatMeterEndpoint(1)}</Typography>
              </Box>
            </Box>
          </Box>
        );
      }

      const value = parseMeters(tr.triggerArgs?.[SENSOR_TRIGGER_ARG_KEYS.DISTANCE]);
      return (
        <Box sx={sliderOuterSx}>
          <Box sx={sliderLabelSx}>
            <Typography variant="body2">{t('controlPanel.sensors.mapping.distance.value', { defaultValue: 'Distance' })}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {formatMeters(value)}
            </Typography>
          </Box>
          <Box sx={sliderInnerSx}>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={value}
              marks={[{ value: 0 }, { value: 1 }]}
              sx={sliderSx}
              valueLabelDisplay="off"
              onChange={(_e, next) => onArgChange(SENSOR_TRIGGER_ARG_KEYS.DISTANCE, String(next))}
            />
            <Box sx={sliderEndpointSx}>
              <Typography variant="body2">{formatMeterEndpoint(0)}</Typography>
              <Typography variant="body2">{formatMeterEndpoint(1)}</Typography>
            </Box>
          </Box>
        </Box>
      );
    },
    [parseMeters, t]
  );

  const currentSaveTriggers = useMemo(() => buildSaveTriggersFrom(triggers), [buildSaveTriggersFrom, triggers]);
  const currentSaveJson = useMemo(() => JSON.stringify(currentSaveTriggers), [currentSaveTriggers]);
  const initialSaveJson = selectedSensorVarName ? initialSaveJsonBySensorVarRef.current[selectedSensorVarName] ?? '[]' : '[]';
  const hasSaveableChanges = currentSaveJson !== initialSaveJson;
  const sensorActionArgsAreValid = useCallback(
    (actionDef: FrameworkAction | null, argValues: Record<string, string>): boolean => {
      const displayArgValues = displayArgValuesForAction(actionDef, argValues);
      const effectiveActionDef = withResolvedMotorVelocityLimits(actionDef, displayArgValues);
      return (effectiveActionDef?.arguments ?? []).every((arg) => {
        const frameworkArg = arg as FrameworkArgument;
        if (isSequenceTupleArg(frameworkArg)) {
          return sequenceTupleRowsAreValid(frameworkArg, displayArgValues, t);
        }
        if (frameworkArg.motorVelocityLimitRpm !== undefined) {
          return scalarFrameworkArgIsValid(frameworkArg, frameworkArg.name, displayArgValues, t);
        }
        return true;
      });
    },
    [displayArgValuesForAction, t]
  );

  // Block saving while existing sequence-of-tuples validation or Motor+ velocity validation has
  // failures, matching the validation shown inline in the row fields.
  const sequenceRowsAllValid = useMemo(
    () =>
      triggers.every((trigger) =>
        [...(trigger.onTrueActions ?? []), ...(trigger.onFalseActions ?? [])].every((action) => {
          if (!action.actionLabel) return true;
          const actionDef = actionDefForLabel(action.actionLabel);
          return sensorActionArgsAreValid(actionDef, action.argValues);
        })
      ),
    [triggers, actionDefForLabel, sensorActionArgsAreValid]
  );
  const canSave = hasSaveableChanges && sequenceRowsAllValid;

  const switchToSensor = useCallback((nextSensorVarName: string) => {
    setSelectedSensorVarName(nextSensorVarName);
  }, []);

  const saveCurrentSensorDraft = useCallback(
    (options?: { closeDialog?: boolean }): boolean => {
      if (!canSave) return false;
      const sensorVarName = selectedSensorVarName || sensorOptions[0]?.sensorVarName || '';
      if (!sensorVarName) return false;
      const draft = { triggers: currentSaveTriggers };
      draftsBySensorVarRef.current[sensorVarName] = draft;
      locallyCachedSensorVarsRef.current.add(sensorVarName);
      initialSaveJsonBySensorVarRef.current[sensorVarName] = currentSaveJson;
      if (options) {
        onSave({ sensorVarName, draft }, options);
      } else {
        onSave({ sensorVarName, draft });
      }
      return true;
    },
    [canSave, currentSaveJson, currentSaveTriggers, onSave, selectedSensorVarName, sensorOptions]
  );

  const handleSave = () => {
    saveCurrentSensorDraft();
  };

  const handleSensorChange = useCallback(
    (e: { target: { value: unknown } }) => {
      const nextSensorVarName = String(e.target.value || '');
      if (!nextSensorVarName || nextSensorVarName === selectedSensorVarName) return;
      if (hasSaveableChanges) {
        setPendingSensorVarName(nextSensorVarName);
        return;
      }
      switchToSensor(nextSensorVarName);
    },
    [hasSaveableChanges, selectedSensorVarName, switchToSensor]
  );

  const discardAndSwitchSensor = useCallback(() => {
    if (!pendingSensorVarName) return;
    switchToSensor(pendingSensorVarName);
    setPendingSensorVarName(null);
  }, [pendingSensorVarName, switchToSensor]);

  const updateAndSwitchSensor = useCallback(() => {
    if (!pendingSensorVarName) return;
    if (!canSave) return;
    if (!saveCurrentSensorDraft({ closeDialog: false })) return;
    switchToSensor(pendingSensorVarName);
    setPendingSensorVarName(null);
  }, [canSave, pendingSensorVarName, saveCurrentSensorDraft, switchToSensor]);

  const renderMappingRows = (trigger: TriggerDraft, triggerIndex: number) => {
    const sideLabels = {
      [SENSOR_ACTION_SIDES.ON_TRUE]: t('controlPanel.sensors.mapping.onTrue', { defaultValue: 'On True' }),
      [SENSOR_ACTION_SIDES.ON_FALSE]: t('controlPanel.sensors.mapping.onFalse', { defaultValue: 'On False' }),
    };
    const rows = mappingRowsForTrigger(trigger);
    return (
      <Stack spacing={1}>
        {rows.map(({ side, action, actionIndex }) => {
          const actionDef = findActionDefForLabel(allAvailableActions, action.actionLabel);
          const displayArgValues = displayArgValuesForAction(actionDef, action.argValues);
          const effectiveActionDef = withResolvedMotorVelocityLimits(actionDef, displayArgValues);
          const actionArgs = effectiveActionDef?.arguments ?? [];
          const actionRowKey = `${triggerIndex}-${side}-${actionIndex}`;
          const rawExpression = String(action.rawExpression || '').trim();
          return (
            <Box
              key={actionRowKey}
              ref={(node: HTMLDivElement | null) => {
                actionRowRefs.current[actionRowKey] = node;
              }}
              data-testid={`sensor-${side}-action-row`}
              data-sensor-mapping-row="true"
              data-focused-action-row={initialActionFocusKey === actionRowKey ? 'true' : undefined}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            >
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Box sx={{ flex: '0 0 128px' }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        size="small"
                        value={side}
                        onChange={(event) => handleMappingSideChange(triggerIndex, side, actionIndex, event.target.value as SensorActionSide)}
                        inputProps={{ 'aria-label': t('controlPanel.sensors.mapping.eventAriaLabel', { defaultValue: 'Sensor event' }) }}
                        sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                      >
                        {Object.values(SENSOR_ACTION_SIDES).map((option) => (
                          <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>
                            {sideLabels[option]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActionSelector
                      value={action.actionLabel}
                      onChange={(next) => handleActionChange(triggerIndex, side, actionIndex, next)}
                      actions={selectableActionList}
                      allowNone
                      noneLabel={t('controller.buttonMapping.dialog.selectAction', { defaultValue: 'Select action' })}
                      ariaLabel={t('controlPanel.sensors.mapping.actionAriaLabel', { defaultValue: 'Mapping action' })}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <IconButton
                      size="small"
                      aria-label={t('controlPanel.sensors.mapping.removeMapping', { defaultValue: 'Remove mapping' })}
                      onClick={() => removeActionAt(triggerIndex, side, actionIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>

                {rawExpression ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('controlPanel.sensors.mapping.unsupportedAction.title', { defaultValue: 'Custom code' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      {t('controlPanel.sensors.mapping.unsupportedAction.description', {
                        defaultValue: 'Unsupported custom code: {{code}}',
                        code: rawExpression,
                      })}
                    </Typography>
                  </Box>
                ) : null}

                {actionArgs.length > 0 ? (
                  <Box>
                    <FrameworkActionArgumentFields
                      actionDef={effectiveActionDef}
                      values={displayArgValues}
                      onArgChange={(name, value) => handleActionArgChange(triggerIndex, side, actionIndex, name, value)}
                      onArgValuesChange={(next) =>
                        updateActionAt(triggerIndex, side, actionIndex, (action) => ({
                          ...action,
                          argValues: mergeSequenceTupleRowChanges(action.argValues, next, actionDefForLabel(action.actionLabel)),
                        }))
                      }
                      pathOptions={pathOptions}
                      enumOptions={ENUM_OPTIONS}
                      t={t}
                      layout="row"
                      showUnits
                      idPrefix={`sensor-${triggerIndex}-${side}-${actionIndex}`}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Box>
          );
        })}
        <Box>
          <Button startIcon={<AddIcon />} onClick={() => handleAddMapping(triggerIndex)} size="small">
            {t('controller.buttonMapping.dialog.addMapping', { defaultValue: 'Add mapping' })}
          </Button>
        </Box>
      </Stack>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { maxHeight: '82vh' } }}>
        <DialogTitle>
          {t('controlPanel.sensors.mapping.title.createSensorInput', {
            defaultValue: 'Create a {{sensorName}} Sensor Input',
            sensorName: selectedSensorVarName || '',
          })}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 1 }}>
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('controlPanel.sensors.mapping.sensorField', { defaultValue: 'Sensor' })}
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  size="small"
                  value={selectedSensorVarName || ''}
                  onChange={handleSensorChange}
                  sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                >
                  {(sensorOptions || []).map((s) => (
                    <MenuItem key={s.sensorVarName} value={s.sensorVarName} sx={{ fontSize: 12 }}>
                      {s.sensorVarName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {t('controlPanel.sensors.mapping.triggerField', { defaultValue: 'Trigger' })}
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  size="small"
                  value={selectedTriggerName && optionNames.has(selectedTriggerName) ? selectedTriggerName : ''}
                  onChange={handleTriggerTypeScreenChange}
                  sx={{ fontSize: 12, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider', borderRadius: 1 } }}
                >
                  {(sensorTriggerOptions || []).map((opt) => (
                    <MenuItem key={opt.name} value={opt.name} sx={{ fontSize: 12 }}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!hasActionOptions ? (
              <Typography variant="body2" color="text.secondary">
                {t('controlPanel.sensors.mapping.noActionsAvailable', {
                  defaultValue: 'No actions are available yet for selection.',
                })}
              </Typography>
            ) : null}

            <Box
              ref={scrollContainerRef}
              data-testid="sensor-mapping-scroll-container"
              sx={{ overflowY: 'auto', pr: 0.5, minHeight: 0, maxHeight: '54vh' }}
            >
              <Stack spacing={1.25}>
                {visibleTriggerEntries.map(({ trigger, triggerIndex }, visibleIndex) => {
                  const hasSelectedTrigger = !!selectedTriggerName && !!selectedTriggerDef;
                  const triggerArgs = selectedTriggerArgs;
                  const rawTriggerArgsText = String(trigger.rawTriggerArgsText || '').trim();
                  const distanceArgsUi = renderDistanceArgSliders(trigger, (name, value) => handleTriggerArgChange(triggerIndex, name, value));
                  const triggerArgsActionDef: FrameworkAction | null =
                    triggerArgs.length > 0
                      ? ({
                          name: trigger.triggerName,
                          arguments: triggerArgs.map((arg) => ({ name: arg.name, type: arg.type } as FrameworkArgument)),
                        } as FrameworkAction)
                      : null;
                  const canRemoveTriggerMapping = canAddThreshold && visibleTriggerEntries.length > 1;
                  const thresholdNumber = visibleIndex + 1;
                  const thresholdTitle =
                    trigger.triggerName === SENSOR_TRIGGER_NAMES.BETWEEN
                      ? t('controlPanel.sensors.mapping.threshold.distanceRangeTitle', {
                          defaultValue: 'Distance range {{index}}',
                          index: thresholdNumber,
                        })
                      : trigger.triggerName === SENSOR_TRIGGER_NAMES.GREATER_THAN || trigger.triggerName === SENSOR_TRIGGER_NAMES.LESS_THAN
                      ? t('controlPanel.sensors.mapping.threshold.distanceTitle', {
                          defaultValue: 'Distance threshold {{index}}',
                          index: thresholdNumber,
                        })
                      : t('controlPanel.sensors.mapping.threshold.title', {
                          defaultValue: 'Threshold {{index}}',
                          index: thresholdNumber,
                        });

                  return (
                    <Box
                      key={`sensor-trigger-${triggerIndex}`}
                      ref={(node: HTMLDivElement | null) => {
                        triggerMappingRefs.current[triggerIndex] = node;
                      }}
                      data-testid="sensor-trigger-mapping"
                      sx={canAddThreshold ? { border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 } : undefined}
                    >
                      <Stack spacing={1.25}>
                        {canAddThreshold ? (
                          <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {thresholdTitle}
                            </Typography>
                            {canRemoveTriggerMapping ? (
                              <IconButton
                                size="small"
                                aria-label={t('controlPanel.sensors.mapping.removeThreshold', { defaultValue: 'Remove threshold' })}
                                onClick={() => handleRemoveMapping(triggerIndex)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Box>
                        ) : null}

                        {hasSelectedTrigger ? (
                          <>
                            {triggerArgs.length > 0 ? (
                              <Box>
                                {rawTriggerArgsText ? (
                                  <Box sx={{ my: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {t('controlPanel.sensors.mapping.unsupportedTriggerArgs.title', { defaultValue: 'Custom code' })}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                      {t('controlPanel.sensors.mapping.unsupportedTriggerArgs.description', {
                                        defaultValue: 'Unsupported trigger arguments: {{code}}',
                                        code: rawTriggerArgsText,
                                      })}
                                    </Typography>
                                  </Box>
                                ) : null}
                                {rawTriggerArgsText ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {t('controlPanel.sensors.mapping.unsupportedTriggerArgs.replacementCaption', {
                                      defaultValue: 'Choose standard replacement trigger arguments',
                                    })}
                                  </Typography>
                                ) : null}
                                {distanceArgsUi ? (
                                  distanceArgsUi
                                ) : triggerArgsActionDef ? (
                                  <FrameworkActionArgumentFields
                                    actionDef={triggerArgsActionDef}
                                    values={trigger.triggerArgs ?? {}}
                                    onArgChange={(name, value) => handleTriggerArgChange(triggerIndex, name, value)}
                                    pathOptions={pathOptions}
                                    enumOptions={ENUM_OPTIONS}
                                    t={t}
                                    layout="row"
                                    showUnits
                                    idPrefix={`sensor-trigger-${triggerIndex}-args`}
                                  />
                                ) : null}
                              </Box>
                            ) : null}

                            {renderMappingRows(trigger, triggerIndex)}
                          </>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {canAddThreshold ? (
              <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button startIcon={<AddIcon />} onClick={handleAddThreshold} size="small">
                  {t('controlPanel.sensors.mapping.addThreshold', { defaultValue: 'Add threshold' })}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!canSave}>
            {t('common.update', { defaultValue: 'Update' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingSensorVarName !== null} onClose={() => setPendingSensorVarName(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('controlPanel.sensors.mapping.unsavedTitle', { defaultValue: 'Unsaved mapping changes' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('controlPanel.sensors.mapping.unsavedBody', {
              defaultValue: 'Update this sensor before switching, or discard the current draft changes.',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSensorVarName(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={discardAndSwitchSensor}>{t('controlPanel.sensors.mapping.discardChanges', { defaultValue: 'Discard changes' })}</Button>
          <Button onClick={updateAndSwitchSensor} variant="contained" disabled={!canSave}>
            {t('common.update', { defaultValue: 'Update' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SensorMappingDialog;
